import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar, Plus, Search, Clock, CheckCircle, Eye, Edit3, UserCheck, Users,
  TrendingUp, TrendingDown, Download, Bell, Award, MapPin,
  MoreVertical, Settings, Shield, Zap, BarChart3, X, Save,
  Brain as BrainIcon, Target, AlertTriangle, Activity,
  ChevronLeft, ChevronRight, Copy, Clipboard, RefreshCw,
  ArrowRightLeft, DollarSign, AlertCircle, Trash2, Send,
  FileText, Sun, Moon, Sunrise, Sunset, User, Briefcase, Printer
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { staffRoster, shiftTypes, rosterStats, staffCredentialRecords } from '../../data/mockData';
import RosterService, { SHIFTS, CARE_MINUTES_TARGET, SHIFT_STATUSES } from '../../services/rostering/RosterService';
import AIRosterEngine from '../../services/rostering/AIRosterEngine';
import HRService from '../../services/hr/HRService';
import { STAFF_ROLES } from '../../data/models/StaffProfile';
import CareMinutesRealTimeDashboard from '../HR/CareMinutesRealTimeDashboard';
import { useConfirm } from '../Common/ConfirmDialog';
import { Tooltip, EmptyState } from '../Common/ReusableUI';
import { TableSkeleton } from '../Common/SkeletonLoaders';

const ROLE_COLORS = {
  rn: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300', dot: 'bg-indigo-500', avatar: 'bg-indigo-600', pill: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  en: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', dot: 'bg-blue-500', avatar: 'bg-blue-600', pill: 'bg-blue-100 text-blue-800 border-blue-200' },
  pca: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', dot: 'bg-emerald-500', avatar: 'bg-emerald-600', pill: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  allied: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', dot: 'bg-purple-500', avatar: 'bg-purple-600', pill: 'bg-purple-100 text-purple-800 border-purple-200' },
  other: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', dot: 'bg-gray-500', avatar: 'bg-gray-500', pill: 'bg-gray-100 text-gray-800 border-gray-200' }
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', published: 'bg-blue-100 text-blue-800', confirmed: 'bg-green-100 text-green-800',
  in_progress: 'bg-yellow-100 text-yellow-800', completed: 'bg-emerald-100 text-emerald-800',
  open: 'bg-orange-100 text-orange-800', cancelled: 'bg-red-100 text-red-800'
};

const SHIFT_ACCENT = {
  AM: { bg: 'bg-amber-500', light: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'Morning', time: '06:00–14:00', icon: Sunrise },
  PM: { bg: 'bg-blue-500', light: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', label: 'Afternoon', time: '14:00–22:00', icon: Sunset },
  NIGHT: { bg: 'bg-indigo-600', light: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', label: 'Night', time: '22:00–06:00', icon: Moon }
};

const EMPLOYMENT_BADGES = {
  permanent: 'bg-slate-100 text-slate-600',
  casual: 'bg-amber-50 text-amber-700',
  agency: 'bg-orange-100 text-orange-700 border border-dashed border-orange-300',
  contract: 'bg-violet-50 text-violet-700'
};

const Roster = () => {
  const { state } = useAppContext();
  const { selectedFacility } = state;

  const [activeTab, setActiveTab] = useState('day');
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1);
    return d.toISOString().split('T')[0];
  });
  const [selectedDay, setSelectedDay] = useState(() => new Date().toISOString().split('T')[0]);
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateShift, setShowCreateShift] = useState(null);
  const [showAssignStaff, setShowAssignStaff] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [costProjection, setCostProjection] = useState(null);
  const [rnCoverage, setRnCoverage] = useState(null);
  const [filterWard, setFilterWard] = useState('all');
  const [filterShift, setFilterShift] = useState('all');
  const [toast, setToast] = useState(null);
  const [swapRequests, setSwapRequests] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState(new Set());
  const confirm = useConfirm();

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const weekEndDate = useMemo(() => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 6);
    return d.toISOString().split('T')[0];
  }, [weekStart]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [weekResult, rnResult, swapResult] = await Promise.all([
        RosterService.getWeeklyView(selectedFacility, weekStart),
        RosterService.check247RNCoverage(selectedFacility, weekStart, weekEndDate),
        RosterService.getSwapRequests()
      ]);
      if (weekResult.success) setWeeklyData(weekResult.data);
      if (rnResult.success) setRnCoverage(rnResult.data);
      if (swapResult.success) setSwapRequests(swapResult.data);
    } catch (error) {
      console.error('[Roster] Load error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFacility, weekStart, weekEndDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAI = useCallback(async () => {
    const result = await AIRosterEngine.generateRecommendations(selectedFacility);
    if (result.success) setAiRecommendations(result.data);
  }, [selectedFacility]);

  const loadCosts = useCallback(async () => {
    const result = await RosterService.calculateCostProjection(selectedFacility, weekStart, weekEndDate);
    if (result.success) setCostProjection(result.data);
  }, [selectedFacility, weekStart, weekEndDate]);

  const navigateWeek = (dir) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + (dir * 7));
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const navigateDay = (dir) => {
    const d = new Date(selectedDay); d.setDate(d.getDate() + dir);
    setSelectedDay(d.toISOString().split('T')[0]);
    const newWeekStart = new Date(d); newWeekStart.setDate(newWeekStart.getDate() - newWeekStart.getDay() + 1);
    const wsStr = newWeekStart.toISOString().split('T')[0];
    if (wsStr !== weekStart) setWeekStart(wsStr);
  };

  const handlePublishWeek = async () => {
    const result = await RosterService.publishRoster(selectedFacility, weekStart, weekEndDate);
    if (result.success) { showToast(`Published ${result.data.published} shifts`); loadData(); }
    else showToast(result.error, 'error');
  };

  const handleCopyWeek = async () => {
    const nextWeek = new Date(weekStart); nextWeek.setDate(nextWeek.getDate() + 7);
    const result = await RosterService.copyWeek(selectedFacility, weekStart, nextWeek.toISOString().split('T')[0], true);
    if (result.success) { showToast(`Copied ${result.data.copied} shifts to next week`); }
    else showToast(result.error, 'error');
  };

  const handleAutoFill = async () => {
    showToast('AI is filling open shifts...', 'info');
    const result = await AIRosterEngine.autoFillWeek(selectedFacility, weekStart);
    if (result.success) {
      showToast(`AI filled ${result.data.totalFilled} of ${result.data.total} shifts (${result.data.confidence}% confidence)`);
      loadData();
    } else showToast(result.error, 'error');
  };

  const handleDeleteShift = async (shiftId) => {
    const confirmed = await confirm({
      title: 'Delete Shift',
      message: 'Delete this shift? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    const result = await RosterService.deleteShift(shiftId);
    if (result.success) {
      showToast('Shift removed');
      setSelectedShifts(prev => { const n = new Set(prev); n.delete(shiftId); return n; });
      loadData();
    }
  };

  const handleConfirmShift = async (shiftId, staffId) => {
    const result = await RosterService.confirmShift(shiftId, staffId);
    if (result.success) { showToast('Shift confirmed'); loadData(); }
  };

  const toggleShiftSelection = (shiftId) => {
    setSelectedShifts(prev => {
      const next = new Set(prev);
      if (next.has(shiftId)) next.delete(shiftId);
      else next.add(shiftId);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedShifts.size === 0) return;
    const confirmed = await confirm({
      title: 'Delete Selected Shifts',
      message: `Delete ${selectedShifts.size} selected shift(s)? This action cannot be undone.`,
      confirmLabel: `Delete ${selectedShifts.size}`,
      variant: 'danger',
    });
    if (!confirmed) return;
    const count = selectedShifts.size;
    for (const id of selectedShifts) {
      await RosterService.deleteShift(id);
    }
    setSelectedShifts(new Set());
    showToast(`${count} shifts deleted`);
    loadData();
  };

  const handleBulkPublish = async () => {
    if (selectedShifts.size === 0) return;
    const count = selectedShifts.size;
    showToast(`Publishing ${count} shifts...`, 'info');
    for (const id of selectedShifts) {
      await RosterService.updateShift(id, { status: 'published' });
    }
    setSelectedShifts(new Set());
    showToast(`${count} shifts published`);
    loadData();
  };

  const exportRosterCSV = () => {
    if (!weeklyData) return;
    const headers = ['Date', 'Day', 'Shift', 'Staff', 'Role', 'Ward', 'Hours', 'Status', 'Employment'];
    const rows = [];
    weeklyData.forEach(day => {
      (day.entries || []).forEach(e => {
        rows.push([day.date, day.dayName, e.shiftName || e.shift, `"${e.staffName || 'Open'}"`, e.roleCode, e.ward, e.hoursScheduled, e.status, e.employmentType]);
      });
    });
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `roster_${weekStart}_${weekEndDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('Roster exported to CSV', 'success');
  };

  const allWards = useMemo(() => {
    if (!weeklyData) return [];
    const wards = new Set();
    weeklyData.forEach(day => day.entries?.forEach(e => wards.add(e.ward)));
    return [...wards].sort();
  }, [weeklyData]);

  const dayData = useMemo(() => {
    if (!weeklyData) return null;
    return weeklyData.find(d => d.date === selectedDay) || null;
  }, [weeklyData, selectedDay]);

  const weekStats = useMemo(() => {
    if (!weeklyData) return { totalShifts: 0, onDutyToday: 0, openShifts: 0, careMinutesPct: 0 };
    const todayStr = new Date().toISOString().split('T')[0];
    const todayData = weeklyData.find(d => d.date === todayStr);
    const totalShifts = weeklyData.reduce((s, d) => s + (d.entries?.length || 0), 0);
    const openShifts = weeklyData.reduce((s, d) => s + (d.openShifts || 0), 0);
    const onDutyToday = todayData?.totalStaff || 0;
    return { totalShifts, onDutyToday, openShifts, careMinutesPct: 87 };
  }, [weeklyData]);

  if (loading && !weeklyData) {
    return (
      <div className="space-y-5">
        <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-700 rounded-2xl p-6 text-white shadow-2xl animate-pulse">
          <div className="h-8 bg-white/20 rounded w-1/3 mb-3" />
          <div className="h-4 bg-white/10 rounded w-1/2" />
          <div className="grid grid-cols-4 gap-3 mt-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white/10 rounded-xl px-4 py-3 h-16" />
            ))}
          </div>
        </div>
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium flex items-center gap-2 transition-all ${
          toast.type === 'error' ? 'bg-red-600 text-white' : toast.type === 'info' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'
        }`}>
          {toast.type === 'success' && <CheckCircle className="w-4 h-4" />}
          {toast.type === 'error' && <AlertCircle className="w-4 h-4" />}
          {toast.type === 'info' && <BrainIcon className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* ═══════════ HEADER ═══════════ */}
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-700 rounded-2xl p-6 text-white shadow-2xl">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
              <Users className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Roster Management</h1>
              <p className="text-indigo-200 text-sm mt-0.5">Interactive scheduling, AI optimization, compliance tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {rnCoverage && !rnCoverage.compliant && (
              <div className="bg-red-500/80 rounded-xl px-3 py-1.5 flex items-center gap-1.5 text-sm font-bold animate-pulse">
                <AlertTriangle className="w-4 h-4" />
                {rnCoverage.gapCount} RN Gap{rnCoverage.gapCount !== 1 ? 's' : ''}
              </div>
            )}
            {rnCoverage?.compliant && (
              <div className="bg-green-500/60 rounded-xl px-3 py-1.5 flex items-center gap-1.5 text-sm font-medium">
                <CheckCircle className="w-4 h-4" /> 24/7 RN ✓
              </div>
            )}
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-4 gap-3 mt-5">
          {[
            { label: 'Total Shifts', value: weekStats.totalShifts, icon: Calendar, trend: '+3%' },
            { label: 'On Duty Today', value: weekStats.onDutyToday, icon: UserCheck, trend: null },
            { label: 'Open Shifts', value: weekStats.openShifts, icon: AlertCircle, trend: weekStats.openShifts > 0 ? 'action' : null },
            { label: 'Care Minutes', value: `${weekStats.careMinutesPct}%`, icon: Activity, trend: '+2%' }
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xl font-bold leading-tight">{stat.value}</div>
                  <div className="text-indigo-200 text-xs">{stat.label}</div>
                </div>
                {stat.trend === 'action' && <AlertTriangle className="w-4 h-4 text-amber-300 ml-auto flex-shrink-0" />}
                {stat.trend && stat.trend !== 'action' && <span className="text-emerald-300 text-xs font-medium ml-auto flex-shrink-0 flex items-center gap-0.5"><TrendingUp className="w-3 h-3" />{stat.trend}</span>}
              </div>
            );
          })}
        </div>

        {/* Action Toolbar */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/15">
          <button onClick={() => setShowCreateShift({ date: selectedDay || new Date().toISOString().split('T')[0] })} className="bg-white text-indigo-700 px-4 py-2 rounded-xl font-semibold text-sm hover:bg-indigo-50 flex items-center gap-1.5 shadow-lg transition-all">
            <Plus className="w-4 h-4" /> New Shift
          </button>
          <button onClick={handleAutoFill} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all">
            <BrainIcon className="w-4 h-4" /> AI Auto-Fill
          </button>
          <button onClick={handlePublishWeek} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all">
            <Send className="w-4 h-4" /> Publish Week
          </button>
          <button onClick={handleCopyWeek} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all">
            <Copy className="w-4 h-4" /> Copy to Next Week
          </button>
          <button onClick={() => window.print()} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all ml-auto">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button onClick={exportRosterCSV} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={loadData} className="bg-white/20 hover:bg-white/30 px-3 py-2 rounded-xl transition-all" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ═══════════ TAB NAVIGATION ═══════════ */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
        <div className="flex space-x-1 p-1.5 overflow-x-auto border-b border-gray-100">
          {[
            { id: 'day', label: 'Day View', icon: Sun },
            { id: 'calendar', label: 'Week Overview', icon: Calendar },
            { id: 'careminutes', label: 'Care Minutes', icon: Activity },
            { id: 'ai', label: 'AI Insights', icon: BrainIcon },
            { id: 'costs', label: 'Cost Projection', icon: DollarSign },
            { id: 'swaps', label: `Swaps (${swapRequests.filter(s => s.status === 'pending').length})`, icon: ArrowRightLeft },
            { id: 'credentials', label: 'Credentials', icon: Shield }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'ai' && !aiRecommendations) loadAI();
                if (tab.id === 'costs' && !costProjection) loadCosts();
              }} className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200'
                  : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50/50'
              }`}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {/* ═══════════ DAY VIEW TAB ═══════════ */}
          {activeTab === 'day' && weeklyData && (
            <DayView
              selectedDay={selectedDay}
              dayData={dayData}
              navigateDay={navigateDay}
              setSelectedDay={setSelectedDay}
              filterWard={filterWard}
              setFilterWard={setFilterWard}
              allWards={allWards}
              setShowCreateShift={setShowCreateShift}
              setShowAssignStaff={setShowAssignStaff}
              handleConfirmShift={handleConfirmShift}
              handleDeleteShift={handleDeleteShift}
              selectedShifts={selectedShifts}
              toggleShiftSelection={toggleShiftSelection}
              handleBulkDelete={handleBulkDelete}
              handleBulkPublish={handleBulkPublish}
              clearSelection={() => setSelectedShifts(new Set())}
            />
          )}

          {/* ═══════════ WEEK OVERVIEW TAB ═══════════ */}
          {activeTab === 'calendar' && weeklyData && (
            <WeekOverview
              weeklyData={weeklyData}
              weekStart={weekStart}
              weekEndDate={weekEndDate}
              navigateWeek={navigateWeek}
              setWeekStart={setWeekStart}
              filterWard={filterWard}
              setFilterWard={setFilterWard}
              filterShift={filterShift}
              setFilterShift={setFilterShift}
              allWards={allWards}
              setShowCreateShift={setShowCreateShift}
              setActiveTab={setActiveTab}
              setSelectedDay={setSelectedDay}
              handleConfirmShift={handleConfirmShift}
              handleDeleteShift={handleDeleteShift}
              setShowAssignStaff={setShowAssignStaff}
            />
          )}

          {/* ═══════════ CARE MINUTES TAB ═══════════ */}
          {activeTab === 'careminutes' && <CareMinutesRealTimeDashboard />}

          {/* ═══════════ AI INSIGHTS TAB ═══════════ */}
          {activeTab === 'ai' && (
            <div className="space-y-6">
              {!aiRecommendations ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BrainIcon className="w-10 h-10 text-purple-400" />
                  </div>
                  <p className="text-gray-500 mb-3">AI-powered roster intelligence</p>
                  <button onClick={loadAI} className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-2.5 rounded-xl font-medium hover:shadow-lg transition-all">Generate AI Analysis</button>
                </div>
              ) : (
                <>
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <BrainIcon className="w-6 h-6 text-purple-600" />
                        <h3 className="text-xl font-bold text-purple-900">AI Roster Intelligence</h3>
                        <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded-full">LIVE</span>
                      </div>
                      <button onClick={loadAI} className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { label: 'Critical', count: aiRecommendations.summary.critical, color: 'red' },
                        { label: 'High', count: aiRecommendations.summary.high, color: 'orange' },
                        { label: 'Medium', count: aiRecommendations.summary.medium, color: 'yellow' },
                        { label: 'Total', count: aiRecommendations.summary.total, color: 'purple' }
                      ].map(s => (
                        <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 text-center shadow-sm">
                          <div className={`text-2xl font-bold ${s.color === 'red' ? 'text-red-700' : s.color === 'orange' ? 'text-orange-700' : s.color === 'yellow' ? 'text-yellow-700' : 'text-purple-700'}`}>{s.count}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {aiRecommendations.recommendations.map(rec => (
                      <div key={rec.id} className={`border rounded-2xl p-5 transition-all hover:shadow-md ${
                        rec.priority === 'Critical' ? 'border-red-200 bg-red-50/50' : rec.priority === 'High' ? 'border-orange-200 bg-orange-50/50' : 'border-gray-200 bg-white'
                      }`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            rec.priority === 'Critical' ? 'bg-red-100' : rec.priority === 'High' ? 'bg-orange-100' : 'bg-yellow-100'
                          }`}>
                            <AlertTriangle className={`w-4 h-4 ${
                              rec.priority === 'Critical' ? 'text-red-600' : rec.priority === 'High' ? 'text-orange-600' : 'text-yellow-600'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${
                                rec.priority === 'Critical' ? 'bg-red-200 text-red-900' : rec.priority === 'High' ? 'bg-orange-200 text-orange-900' : 'bg-yellow-200 text-yellow-900'
                              }`}>{rec.priority}</span>
                              <span className="text-xs text-gray-500">{rec.type}</span>
                              <Tooltip content={`AI confidence: ${rec.aiConfidence}% — ${rec.aiConfidence >= 90 ? 'High certainty' : rec.aiConfidence >= 70 ? 'Moderate certainty' : 'Low certainty, review recommended'}`}><span className="text-xs text-purple-600 flex items-center gap-0.5 ml-auto cursor-help"><BrainIcon className="w-3 h-3" />{rec.aiConfidence}%</span></Tooltip>
                            </div>
                            <h4 className="font-semibold text-sm text-gray-900">{rec.title}</h4>
                            {rec.description && <p className="text-xs text-gray-600 mt-1">{rec.description}</p>}
                            {rec.impact && <p className="text-xs text-gray-500 mt-1 italic">{rec.impact}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                    {aiRecommendations.recommendations.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                        <p className="font-medium">All clear — no issues detected.</p>
                      </div>
                    )}
                  </div>

                  {aiRecommendations.staffMix && (
                    <div className="bg-white border border-gray-200 rounded-2xl p-6">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-indigo-600" /> Optimal Daily Staff Mix</h3>
                      <div className="grid grid-cols-3 gap-4">
                        {Object.entries(aiRecommendations.staffMix.recommendation.perShift).map(([shift, roles]) => {
                          const accent = SHIFT_ACCENT[shift];
                          return (
                            <div key={shift} className={`${accent?.light || 'bg-gray-50'} rounded-xl p-4 border ${accent?.border || 'border-gray-200'}`}>
                              <div className="flex items-center gap-2 mb-3">
                                <div className={`w-1 h-6 rounded-full ${accent?.bg || 'bg-gray-400'}`} />
                                <div className="font-semibold text-sm">{SHIFTS[shift]?.name || shift}</div>
                              </div>
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between items-center"><span className="text-indigo-600 font-medium">RN</span><span className="font-bold bg-white rounded px-2 py-0.5">{roles.rn}</span></div>
                                <div className="flex justify-between items-center"><span className="text-blue-600 font-medium">EN</span><span className="font-bold bg-white rounded px-2 py-0.5">{roles.en}</span></div>
                                <div className="flex justify-between items-center"><span className="text-emerald-600 font-medium">PCA</span><span className="font-bold bg-white rounded px-2 py-0.5">{roles.pca}</span></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-4 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                        Estimated daily cost: <strong>${aiRecommendations.staffMix.estimatedDailyCost?.toLocaleString()}</strong> &bull; Based on {aiRecommendations.staffMix.residentCount} residents
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ═══════════ COST PROJECTION TAB ═══════════ */}
          {activeTab === 'costs' && (
            <div className="space-y-6">
              {!costProjection ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DollarSign className="w-10 h-10 text-green-400" />
                  </div>
                  <p className="text-gray-500 mb-3">Weekly labour cost breakdown</p>
                  <button onClick={loadCosts} className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-2.5 rounded-xl font-medium hover:shadow-lg transition-all">Calculate Costs</button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {[
                      { label: 'Total Cost', value: `$${costProjection.totalCost.toLocaleString()}`, color: 'indigo', icon: DollarSign },
                      { label: 'Base Pay', value: `$${costProjection.baseCost.toLocaleString()}`, color: 'blue', icon: Briefcase },
                      { label: 'Penalties', value: `$${costProjection.penaltyCost.toLocaleString()}`, color: 'orange', icon: AlertTriangle },
                      { label: 'Agency Premium', value: `$${costProjection.agencyCost.toLocaleString()}`, color: 'red', icon: Users },
                      { label: 'Avg/Shift', value: `$${costProjection.avgCostPerShift}`, color: 'green', icon: BarChart3 }
                    ].map(card => {
                      const Icon = card.icon;
                      const bgMap = { indigo: 'bg-indigo-50 border-indigo-200', blue: 'bg-blue-50 border-blue-200', orange: 'bg-orange-50 border-orange-200', red: 'bg-red-50 border-red-200', green: 'bg-green-50 border-green-200' };
                      const txtMap = { indigo: 'text-indigo-700', blue: 'text-blue-700', orange: 'text-orange-700', red: 'text-red-700', green: 'text-green-700' };
                      return (
                        <div key={card.label} className={`${bgMap[card.color]} border rounded-2xl p-4`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className={`w-4 h-4 ${txtMap[card.color]}`} />
                            <span className="text-xs text-gray-500">{card.label}</span>
                          </div>
                          <div className={`text-xl font-bold ${txtMap[card.color]}`}>{card.value}</div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                      <h3 className="font-bold text-gray-900">Shift Cost Breakdown</h3>
                      <span className="text-sm text-gray-500">{costProjection.totalShifts} shifts &bull; {costProjection.totalHours}h total</span>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Date</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Staff</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Role</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Shift</th>
                            <th className="px-3 py-2.5 text-right font-bold text-gray-600">Rate</th>
                            <th className="px-3 py-2.5 text-right font-bold text-gray-600">Multiplier</th>
                            <th className="px-3 py-2.5 text-right font-bold text-gray-600">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {costProjection.breakdown.slice(0, 50).map((row, i) => (
                            <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/80 transition-colors">
                              <td className="px-3 py-2">{row.date}</td>
                              <td className="px-3 py-2 font-medium">{row.staffName}</td>
                              <td className="px-3 py-2">{row.role}</td>
                              <td className="px-3 py-2">{row.shift} ({row.hours}h)</td>
                              <td className="px-3 py-2 text-right">${row.baseRate}</td>
                              <td className="px-3 py-2 text-right">
                                <span className={parseFloat(row.multiplier) > 1 ? 'text-orange-600 font-bold' : ''}>{row.multiplier}x</span>
                                {row.penaltyLabel !== 'Base' && <span className="text-[9px] text-gray-400 ml-1">({row.penaltyLabel})</span>}
                              </td>
                              <td className="px-3 py-2 text-right font-bold">${row.cost}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ═══════════ SWAP REQUESTS TAB ═══════════ */}
          {activeTab === 'swaps' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-800">
                Staff can request shift swaps. Both parties must be qualified. Manager approves.
              </div>
              {swapRequests.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ArrowRightLeft className="w-10 h-10 text-gray-300" />
                  </div>
                  <p className="font-medium">No swap requests</p>
                  <p className="text-xs text-gray-400 mt-1">Staff shift swap requests will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {swapRequests.map(swap => (
                    <div key={swap.id} className="border border-gray-200 rounded-2xl p-4 flex items-center justify-between hover:shadow-md transition-all">
                      <div className="flex items-center gap-3">
                        <div className="flex -space-x-2">
                          <div className="w-9 h-9 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white">{swap.requestorName?.split(' ').map(n => n[0]).join('')}</div>
                          <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white">{swap.targetName?.split(' ').map(n => n[0]).join('')}</div>
                        </div>
                        <div>
                          <div className="font-semibold text-sm">{swap.requestorName} ↔ {swap.targetName}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{swap.reason || 'No reason provided'}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${swap.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : swap.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {swap.status}
                        </span>
                        {swap.status === 'pending' && (
                          <button onClick={async () => { await RosterService.approveSwap(swap.id, 'admin'); showToast('Swap approved'); loadData(); }} className="bg-green-500 hover:bg-green-600 text-white px-4 py-1.5 rounded-xl text-xs font-medium transition-all">Approve</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══════════ CREDENTIALS TAB ═══════════ */}
          {activeTab === 'credentials' && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4 text-sm text-indigo-900">
                <strong>QS 8 — Workforce.</strong> Registration and screening due dates. Staff with expired credentials are auto-blocked from rostering.
              </div>
              <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs font-bold uppercase text-slate-600">
                    <tr>
                      <th className="px-4 py-3">Staff</th><th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">AHPRA / ID</th><th className="px-4 py-3">AHPRA exp.</th>
                      <th className="px-4 py-3">WWCC exp.</th><th className="px-4 py-3">Police check</th>
                      <th className="px-4 py-3">Mandatory training</th><th className="px-4 py-3">Flu due</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffCredentialRecords.map(row => (
                      <tr key={row.staffId} className="border-t border-gray-100 hover:bg-gray-50/80 transition-colors">
                        <td className="px-4 py-2.5 font-medium text-gray-900">{row.name}</td>
                        <td className="px-4 py-2.5 text-gray-700">{row.role}</td>
                        <td className="px-4 py-2.5 font-mono text-xs">{row.ahpraRegistrationId}</td>
                        <td className="px-4 py-2.5">{row.ahpraExpiry || '—'}</td>
                        <td className="px-4 py-2.5">{row.wwccExpiry}</td>
                        <td className="px-4 py-2.5">{row.policeCheckExpiry}</td>
                        <td className="px-4 py-2.5">{row.mandatoryTrainingExpiry}</td>
                        <td className="px-4 py-2.5">{row.influenzaVaccineDue}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${
                            row.status === 'expired' ? 'bg-red-100 text-red-900 border-red-200' : row.status === 'warning' ? 'bg-amber-100 text-amber-900 border-amber-200' : 'bg-emerald-100 text-emerald-900 border-emerald-200'
                          }`}>{row.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════ CREATE SHIFT MODAL ═══════════ */}
      {showCreateShift && (
        <CreateShiftModal
          initialDate={showCreateShift.date}
          initialShift={showCreateShift.shift}
          facilityId={selectedFacility}
          wards={allWards}
          onClose={() => setShowCreateShift(null)}
          onCreated={() => { setShowCreateShift(null); showToast('Shift created'); loadData(); }}
        />
      )}

      {/* ═══════════ SHIFT DETAIL MODAL ═══════════ */}
      {showAssignStaff && (
        <ShiftDetailModal
          entry={showAssignStaff}
          facilityId={selectedFacility}
          onClose={() => setShowAssignStaff(null)}
          onUpdated={() => { setShowAssignStaff(null); showToast('Shift updated'); loadData(); }}
        />
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// DAY VIEW
// ═══════════════════════════════════════════════════════════════════

const DayView = ({ selectedDay, dayData, navigateDay, setSelectedDay, filterWard, setFilterWard, allWards, setShowCreateShift, setShowAssignStaff, handleConfirmShift, handleDeleteShift, selectedShifts, toggleShiftSelection, handleBulkDelete, handleBulkPublish, clearSelection }) => {
  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = selectedDay === todayStr;
  const dateObj = new Date(selectedDay + 'T12:00:00');
  const dateDisplay = dateObj.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const shiftColumns = useMemo(() => {
    if (!dayData) return { AM: [], PM: [], NIGHT: [] };
    let entries = dayData.entries || [];
    if (filterWard !== 'all') entries = entries.filter(e => e.ward === filterWard);
    return {
      AM: entries.filter(e => e.shift === 'AM'),
      PM: entries.filter(e => e.shift === 'PM'),
      NIGHT: entries.filter(e => e.shift === 'NIGHT')
    };
  }, [dayData, filterWard]);

  const getCoverage = (entries) => {
    const rn = entries.filter(e => e.careMinuteCategory === 'rn').length;
    const en = entries.filter(e => e.careMinuteCategory === 'en').length;
    const pca = entries.filter(e => e.careMinuteCategory === 'pca').length;
    return { rn, en, pca };
  };

  const totalStaff = (dayData?.entries || []).length;
  const totalHours = (dayData?.entries || []).reduce((s, e) => s + (e.hoursScheduled || 0), 0);
  const agencyCount = (dayData?.entries || []).filter(e => e.employmentType === 'agency').length;

  return (
    <div className="space-y-4">
      {/* Date Picker Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigateDay(-1)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"><ChevronLeft className="w-5 h-5 text-gray-600" /></button>
          <div className="text-center">
            <div className={`text-lg font-bold ${isToday ? 'text-indigo-700' : 'text-gray-900'}`}>{dateDisplay}</div>
            {isToday && <span className="text-xs font-medium text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-full">Today</span>}
          </div>
          <button onClick={() => navigateDay(1)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"><ChevronRight className="w-5 h-5 text-gray-600" /></button>
          {!isToday && (
            <button onClick={() => setSelectedDay(todayStr)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors ml-2">Go to Today</button>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <input type="date" value={selectedDay} onChange={e => setSelectedDay(e.target.value)} className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          <select value={filterWard} onChange={e => setFilterWard(e.target.value)} className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white">
            <option value="all">All Wards</option>
            {allWards.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </div>

      {/* Three-Column Shift Layout */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(SHIFT_ACCENT).map(([shiftCode, accent]) => {
          const entries = shiftColumns[shiftCode] || [];
          const coverage = getCoverage(entries);
          const hasRN = coverage.rn > 0;
          const ShiftIcon = accent.icon;

          return (
            <div key={shiftCode} className={`rounded-2xl border-2 ${accent.border} overflow-hidden flex flex-col`}>
              {/* Shift Header */}
              <div className={`${accent.bg} px-4 py-3 flex items-center justify-between`}>
                <div className="flex items-center gap-2 text-white">
                  <ShiftIcon className="w-5 h-5" />
                  <div>
                    <div className="font-bold text-sm">{accent.label}</div>
                    <div className="text-xs opacity-80">{accent.time}</div>
                  </div>
                </div>
                <div className="bg-white/20 rounded-lg px-2 py-1 text-xs text-white font-bold">
                  {entries.length} staff
                </div>
              </div>

              {/* RN Warning Banner */}
              {!hasRN && entries.length > 0 && (
                <div className="bg-red-500 text-white px-3 py-2 text-xs font-bold flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> No RN assigned — 24/7 coverage breach
                </div>
              )}

              {/* Coverage Summary with tooltips */}
              <div className={`px-3 py-2 ${accent.light} border-b ${accent.border} flex items-center gap-3 text-xs`}>
                <Tooltip content={coverage.rn > 0 ? `${coverage.rn} Registered Nurse(s) — 24/7 RN coverage met` : 'No RN assigned — 24/7 coverage breach'}>
                  <span className={`flex items-center gap-1 font-medium cursor-help ${coverage.rn > 0 ? 'text-green-700' : 'text-red-600'}`}>
                    RN: {coverage.rn} {coverage.rn > 0 ? '✓' : '✗'}
                  </span>
                </Tooltip>
                <Tooltip content={`${coverage.en} Enrolled Nurse(s) assigned this shift`}>
                  <span className={`flex items-center gap-1 font-medium cursor-help ${coverage.en > 0 ? 'text-green-700' : 'text-amber-600'}`}>
                    EN: {coverage.en} {coverage.en > 0 ? '✓' : '–'}
                  </span>
                </Tooltip>
                <Tooltip content={`${coverage.pca} Personal Care Assistant(s) assigned this shift`}>
                  <span className={`flex items-center gap-1 font-medium cursor-help ${coverage.pca > 0 ? 'text-green-700' : 'text-amber-600'}`}>
                    PCA: {coverage.pca} {coverage.pca > 0 ? '✓' : '–'}
                  </span>
                </Tooltip>
              </div>

              {/* Staff Cards */}
              <div className="flex-1 p-3 space-y-2 overflow-y-auto max-h-[500px] bg-white">
                {entries.map(entry => {
                  const roleColor = ROLE_COLORS[entry.careMinuteCategory] || ROLE_COLORS.other;
                  const initials = entry.staffName?.split(' ').map(n => n[0]).join('') || '?';
                  const isAgency = entry.employmentType === 'agency';

                  return (
                    <div
                      key={entry.id}
                      className={`group relative rounded-2xl border p-3 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 ${roleColor.bg} ${roleColor.border} ${isAgency ? 'border-dashed' : ''}`}
                      onClick={() => setShowAssignStaff(entry)}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedShifts?.has(entry.id) || false}
                          onChange={() => toggleShiftSelection(entry.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-3 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0 cursor-pointer"
                        />
                        <div className={`w-10 h-10 rounded-full ${roleColor.avatar} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-gray-900 truncate">{entry.staffName || 'Open Shift'}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${roleColor.pill}`}>{entry.roleCode}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{entry.ward}</span>
                            <span>{entry.hoursScheduled}h</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${STATUS_COLORS[entry.status] || ''}`}>{entry.status}</span>
                            {entry.employmentType && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${EMPLOYMENT_BADGES[entry.employmentType] || 'bg-gray-100 text-gray-600'}`}>{entry.employmentType}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Hover Actions */}
                      <div className="hidden group-hover:flex absolute top-2 right-2 gap-1">
                        {entry.status === 'published' && (
                          <button onClick={e => { e.stopPropagation(); handleConfirmShift(entry.id, entry.staffId); }} className="bg-green-500 hover:bg-green-600 text-white p-1.5 rounded-lg shadow transition-all" title="Confirm">
                            <CheckCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={e => { e.stopPropagation(); handleDeleteShift(entry.id); }} className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-lg shadow transition-all" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* (e) Empty state with EmptyState component */}
                {entries.length === 0 && (
                  <EmptyState
                    icon={Users}
                    title="No staff assigned"
                    description={`No shifts scheduled for ${accent.label.toLowerCase()} period`}
                    action={
                      <button onClick={() => setShowCreateShift({ date: selectedDay, shift: shiftCode })} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 mt-2">
                        <Plus className="w-3 h-3" /> Add shift
                      </button>
                    }
                  />
                )}

                {/* Add shift card */}
                {entries.length > 0 && (
                  <button
                    onClick={() => setShowCreateShift({ date: selectedDay, shift: shiftCode })}
                    className="w-full rounded-2xl border-2 border-dashed border-gray-200 p-3 text-center text-sm text-gray-400 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all flex items-center justify-center gap-1.5"
                  >
                    <Plus className="w-4 h-4" /> Add Staff
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Day Summary Footer */}
      <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 flex items-center justify-between">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Total Staff: <strong className="text-gray-900">{totalStaff}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Total Hours: <strong className="text-gray-900">{totalHours}h</strong></span>
          </div>
          {agencyCount > 0 && (
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-orange-400" />
              <span className="text-orange-600">Agency: <strong>{agencyCount}</strong></span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {Object.entries({ RN: 'indigo', EN: 'blue', PCA: 'emerald', Allied: 'purple' }).map(([label, color]) => (
            <div key={label} className="flex items-center gap-1">
              <div className={`w-2.5 h-2.5 rounded-full bg-${color}-500`} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* (b) Floating bulk action bar */}
      {selectedShifts && selectedShifts.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-indigo-700 text-white rounded-2xl shadow-2xl px-6 py-3 flex items-center gap-4 border border-indigo-500">
          <span className="font-semibold text-sm">{selectedShifts.size} shift{selectedShifts.size !== 1 ? 's' : ''} selected</span>
          <div className="h-5 w-px bg-indigo-400" />
          <button onClick={handleBulkPublish} className="bg-white/20 hover:bg-white/30 px-4 py-1.5 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all">
            <Send className="w-3.5 h-3.5" /> Publish Selected
          </button>
          <button onClick={handleBulkDelete} className="bg-red-500/80 hover:bg-red-500 px-4 py-1.5 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all">
            <Trash2 className="w-3.5 h-3.5" /> Delete Selected
          </button>
          <button onClick={clearSelection} className="bg-white/10 hover:bg-white/20 p-1.5 rounded-lg transition-all" title="Clear selection">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// WEEK OVERVIEW — Row-based by shift period
// ═══════════════════════════════════════════════════════════════════

const WeekOverview = ({ weeklyData, weekStart, weekEndDate, navigateWeek, setWeekStart, filterWard, setFilterWard, filterShift, setFilterShift, allWards, setShowCreateShift, setActiveTab, setSelectedDay, handleConfirmShift, handleDeleteShift, setShowAssignStaff }) => {
  const todayStr = new Date().toISOString().split('T')[0];

  const filteredData = useMemo(() => {
    if (!weeklyData) return [];
    return weeklyData.map(day => {
      let entries = day.entries || [];
      if (filterWard !== 'all') entries = entries.filter(e => e.ward === filterWard);
      if (filterShift !== 'all') entries = entries.filter(e => e.shift === filterShift);
      return { ...day, filteredEntries: entries };
    });
  }, [weeklyData, filterWard, filterShift]);

  const switchToDay = (date, shiftCode) => {
    setSelectedDay(date);
    setActiveTab('day');
  };

  const StaffPill = ({ entry }) => {
    const roleColor = ROLE_COLORS[entry.careMinuteCategory] || ROLE_COLORS.other;
    const initials = entry.staffName?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setShowAssignStaff(entry); }}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border transition-all hover:shadow-sm ${roleColor.pill} ${entry.employmentType === 'agency' ? 'border-dashed' : ''}`}
        title={`${entry.staffName} (${entry.roleCode}) — ${entry.status}`}
      >
        <span>{initials}</span>
        <span className="opacity-70">{entry.roleCode}</span>
      </button>
    );
  };

  const getCoverageColor = (entries, shiftCode) => {
    const shiftEntries = entries.filter(e => e.shift === shiftCode);
    const rnCount = shiftEntries.filter(e => e.careMinuteCategory === 'rn').length;
    if (shiftEntries.length === 0) return 'bg-gray-300';
    if (rnCount === 0) return 'bg-red-500';
    if (shiftEntries.length < 3) return 'bg-amber-400';
    return 'bg-green-500';
  };

  return (
    <div className="space-y-4">
      {/* Navigation + Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => navigateWeek(-1)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"><ChevronLeft className="w-5 h-5" /></button>
          <span className="font-bold text-gray-900">
            {new Date(weekStart).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} — {new Date(weekEndDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          <button onClick={() => navigateWeek(1)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"><ChevronRight className="w-5 h-5" /></button>
          <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1); setWeekStart(d.toISOString().split('T')[0]); }} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium bg-indigo-50 px-3 py-1.5 rounded-lg ml-2 transition-colors">Today</button>
        </div>
        <div className="flex gap-2">
          <select value={filterWard} onChange={e => setFilterWard(e.target.value)} className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white">
            <option value="all">All Wards</option>
            {allWards.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
          <select value={filterShift} onChange={e => setFilterShift(e.target.value)} className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white">
            <option value="all">All Shifts</option>
            {Object.entries(SHIFTS).map(([code, s]) => <option key={code} value={code}>{s.name}</option>)}
          </select>
        </div>
      </div>

      {/* Week Grid — header + shift rows */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-[140px_repeat(7,1fr)] bg-gray-50 border-b border-gray-200">
          <div className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase">Shift Period</div>
          {filteredData.map(day => {
            const isDayToday = day.date === todayStr;
            return (
              <div key={day.date} className={`px-2 py-2.5 text-center border-l border-gray-200 ${isDayToday ? 'bg-indigo-50' : ''}`}>
                <div className="text-xs font-bold text-gray-500">{day.dayName}</div>
                <div className={`text-sm font-bold mt-0.5 ${isDayToday ? 'text-indigo-700' : 'text-gray-900'}`}>
                  <span className={isDayToday ? 'bg-indigo-600 text-white w-7 h-7 rounded-full inline-flex items-center justify-center' : ''}>
                    {day.dayOfMonth}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-1 mt-1">
                  {day.hasRNGap && <AlertTriangle className="w-3 h-3 text-red-500" />}
                  {day.isPublicHoliday && <span className="text-[8px] bg-red-100 text-red-700 rounded px-1 font-bold">PH</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Shift Period Rows */}
        {Object.entries(SHIFT_ACCENT).map(([shiftCode, accent]) => {
          if (filterShift !== 'all' && filterShift !== shiftCode) return null;
          const ShiftIcon = accent.icon;
          return (
            <div key={shiftCode} className="grid grid-cols-[140px_repeat(7,1fr)] border-b border-gray-100 last:border-b-0">
              {/* Row Header */}
              <div className={`px-3 py-3 flex items-center gap-2 ${accent.light} border-r border-gray-200`}>
                <div className={`w-1 h-8 rounded-full ${accent.bg}`} />
                <div>
                  <div className="text-xs font-bold text-gray-700 flex items-center gap-1"><ShiftIcon className="w-3.5 h-3.5" />{accent.label}</div>
                  <div className="text-[10px] text-gray-400">{accent.time}</div>
                </div>
              </div>

              {/* 7 Day Cells */}
              {filteredData.map(day => {
                const isDayToday = day.date === todayStr;
                const cellEntries = day.filteredEntries.filter(e => e.shift === shiftCode);
                const MAX_PILLS = 4;
                const overflow = cellEntries.length > MAX_PILLS ? cellEntries.length - MAX_PILLS : 0;
                const displayEntries = overflow > 0 ? cellEntries.slice(0, MAX_PILLS) : cellEntries;

                return (
                  <div
                    key={day.date}
                    className={`px-2 py-2 border-l border-gray-100 min-h-[70px] cursor-pointer hover:bg-gray-50/50 transition-colors relative group ${isDayToday ? 'bg-indigo-50/30' : ''}`}
                    onClick={() => switchToDay(day.date, shiftCode)}
                  >
                    {/* Coverage Indicator */}
                    <div className="flex items-center justify-between mb-1.5">
                      <Tooltip content={`${cellEntries.length} staff — RN:${cellEntries.filter(e => e.careMinuteCategory === 'rn').length} EN:${cellEntries.filter(e => e.careMinuteCategory === 'en').length} PCA:${cellEntries.filter(e => e.careMinuteCategory === 'pca').length}`}>
                        <div className={`w-2 h-2 rounded-full cursor-help ${getCoverageColor(day.filteredEntries, shiftCode)}`} />
                      </Tooltip>
                      <button
                        onClick={e => { e.stopPropagation(); setShowCreateShift({ date: day.date, shift: shiftCode }); }}
                        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-indigo-100 rounded transition-all"
                        title="Add shift"
                      >
                        <Plus className="w-3 h-3 text-indigo-500" />
                      </button>
                    </div>

                    {/* Staff Pills */}
                    <div className="flex flex-wrap gap-0.5">
                      {displayEntries.map(entry => <StaffPill key={entry.id} entry={entry} />)}
                      {overflow > 0 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-500 border border-gray-200">
                          +{overflow}
                        </span>
                      )}
                    </div>

                    {cellEntries.length === 0 && (
                      <div className="text-[10px] text-gray-300 text-center pt-2">—</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Footer Row — Per-day totals */}
        <div className="grid grid-cols-[140px_repeat(7,1fr)] bg-gray-50 border-t border-gray-200">
          <div className="px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Day Totals</div>
          {filteredData.map(day => (
            <div key={day.date} className="px-2 py-2 border-l border-gray-200 text-center">
              <div className="text-[10px] text-gray-500">{day.filteredEntries.length} staff</div>
              <div className="flex items-center justify-center gap-1 mt-0.5">
                <span className="text-[10px] text-indigo-600 font-bold">RN:{day.byRole.rn}</span>
                <span className="text-[10px] text-blue-600">EN:{day.byRole.en}</span>
                <span className="text-[10px] text-emerald-600">PCA:{day.byRole.pca}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-5 text-xs text-gray-500 px-1">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-green-500" /> Fully staffed</span>
          <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Understaffed</span>
          <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-red-500" /> No RN</span>
        </div>
        <div className="h-3 w-px bg-gray-300" />
        {Object.entries({ RN: 'indigo', EN: 'blue', PCA: 'emerald', Allied: 'purple' }).map(([label, color]) => (
          <div key={label} className="flex items-center gap-1">
            <div className={`w-2.5 h-2.5 rounded-full bg-${color}-500`} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// CREATE SHIFT MODAL — Enhanced two-panel layout
// ═══════════════════════════════════════════════════════════════════

const CreateShiftModal = ({ initialDate, initialShift, facilityId, wards, onClose, onCreated }) => {
  const [form, setForm] = useState({
    date: initialDate, shift: initialShift || 'AM', ward: wards[0] || 'Ward A',
    roleCode: 'RN', notes: '', breakMinutes: 30,
    customStart: '', customEnd: '', useCustomTime: false
  });
  const [availableStaff, setAvailableStaff] = useState(null);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');
  const [error, setError] = useState('');

  const loadAvailableStaff = async () => {
    setLoadingStaff(true);
    const result = await RosterService.getAvailableStaff(facilityId, form.date, form.shift, form.roleCode);
    if (result.success) setAvailableStaff(result.data);
    setLoadingStaff(false);
  };

  useEffect(() => { loadAvailableStaff(); }, [form.date, form.shift, form.roleCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateOpen = async () => {
    const result = await RosterService.createShift({ ...form, facilityId });
    if (result.success) onCreated();
    else setError(result.error);
  };

  const handleAssignAndCreate = async (staff) => {
    const result = await RosterService.createShift({
      ...form, facilityId,
      staffId: staff.staffId, staffName: staff.staffName,
      roleCode: staff.roleCode, employmentType: staff.employmentType
    });
    if (result.success) onCreated();
    else setError(result.error);
  };

  const filteredStaff = useMemo(() => {
    if (!availableStaff) return [];
    if (!staffSearch) return availableStaff;
    const term = staffSearch.toLowerCase();
    return availableStaff.filter(s =>
      s.staffName.toLowerCase().includes(term) ||
      s.roleCode.toLowerCase().includes(term) ||
      s.employmentType.toLowerCase().includes(term)
    );
  }, [availableStaff, staffSearch]);

  const selectedShiftInfo = SHIFTS[form.shift];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-purple-50">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center"><Plus className="w-4 h-4 text-indigo-600" /></div>
            Create Shift
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-xl transition-all"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex max-h-[calc(90vh-72px)]">
          {/* Left Panel — Shift Config */}
          <div className="w-1/2 p-5 border-r border-gray-100 overflow-y-auto space-y-4">
            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Shift</label>
                <select value={form.shift} onChange={e => setForm({ ...form, shift: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  {Object.entries(SHIFTS).map(([code, s]) => <option key={code} value={code}>{s.name} ({s.start}–{s.end})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ward</label>
                <select value={form.ward} onChange={e => setForm({ ...form, ward: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  {wards.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Role Required</label>
                <select value={form.roleCode} onChange={e => setForm({ ...form, roleCode: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  {Object.entries(STAFF_ROLES).map(([code, role]) => <option key={code} value={code}>{role.name}</option>)}
                </select>
              </div>
            </div>

            {/* Custom Time */}
            <div>
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input type="checkbox" checked={form.useCustomTime} onChange={e => setForm({ ...form, useCustomTime: e.target.checked })} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                <span>Custom time (override preset)</span>
              </label>
              {form.useCustomTime && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start</label>
                    <input type="time" value={form.customStart || selectedShiftInfo?.start || ''} onChange={e => setForm({ ...form, customStart: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End</label>
                    <input type="time" value={form.customEnd || selectedShiftInfo?.end || ''} onChange={e => setForm({ ...form, customEnd: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm" />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500" placeholder="Optional shift notes..." />
            </div>

            <button onClick={handleCreateOpen} className="w-full bg-orange-50 border-2 border-dashed border-orange-300 text-orange-700 py-3 rounded-xl font-medium text-sm hover:bg-orange-100 transition-all flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Create as Open Shift (unassigned)
            </button>

            {/* Shift Preview */}
            {selectedShiftInfo && (
              <div className={`${SHIFT_ACCENT[form.shift]?.light || 'bg-gray-50'} rounded-xl p-3 border ${SHIFT_ACCENT[form.shift]?.border || 'border-gray-200'}`}>
                <div className="text-xs font-medium text-gray-500 mb-1">Shift Preview</div>
                <div className="flex items-center gap-3 text-sm">
                  <div className={`w-1.5 h-8 rounded-full ${SHIFT_ACCENT[form.shift]?.bg || 'bg-gray-400'}`} />
                  <div>
                    <span className="font-bold">{selectedShiftInfo.name}</span>
                    <span className="text-gray-500 ml-2">{form.useCustomTime ? `${form.customStart || selectedShiftInfo.start}–${form.customEnd || selectedShiftInfo.end}` : `${selectedShiftInfo.start}–${selectedShiftInfo.end}`}</span>
                    <span className="text-gray-400 ml-2">{selectedShiftInfo.hours}h</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel — Available Staff */}
          <div className="w-1/2 overflow-y-auto bg-gray-50/50 flex flex-col">
            <div className="p-4 border-b border-gray-100 bg-white sticky top-0 z-10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm text-gray-900">Available Staff</h3>
                {loadingStaff && <span className="text-xs text-gray-400 animate-pulse">Loading...</span>}
              </div>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={staffSearch}
                  onChange={e => setStaffSearch(e.target.value)}
                  placeholder="Search staff..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="p-3 space-y-2 flex-1">
              {filteredStaff.map(staff => {
                const roleColor = ROLE_COLORS[STAFF_ROLES[staff.roleCode]?.careMinuteCategory] || ROLE_COLORS.other;
                const initials = staff.staffName.split(' ').map(n => n[0]).join('');
                const hoursPercent = Math.min(100, (staff.weeklyHours / 38) * 100);
                return (
                  <button
                    key={staff.staffId}
                    onClick={() => handleAssignAndCreate(staff)}
                    disabled={!staff.eligible}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      staff.eligible ? 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-md cursor-pointer' : 'bg-gray-50 border-gray-100 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${staff.eligible ? roleColor.avatar : 'bg-gray-400'} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm truncate">{staff.staffName}</span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${roleColor.pill}`}>{staff.roleCode}</span>
                        </div>
                        <div className="text-xs text-gray-500">{staff.roleName} &bull; {staff.employmentType}</div>
                        {/* Weekly hours bar */}
                        <div className="flex items-center gap-2 mt-1.5">
                          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${hoursPercent > 90 ? 'bg-red-500' : hoursPercent > 70 ? 'bg-amber-500' : 'bg-indigo-500'}`} style={{ width: `${hoursPercent}%` }} />
                          </div>
                          <span className="text-[10px] text-gray-400 w-12 text-right">{staff.weeklyHours}h/38</span>
                        </div>
                      </div>
                    </div>
                    {/* Warning badges */}
                    {(staff.overtimeWarning || staff.consecutiveWarning || staff.fatigueWarning || !staff.eligible) && (
                      <div className="flex gap-1 mt-2 ml-13">
                        {staff.overtimeWarning && <Tooltip content="Staff has exceeded the 38h/week threshold — overtime rates may apply"><span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium cursor-help">Overtime</span></Tooltip>}
                        {staff.consecutiveWarning && <Tooltip content={`Working ${staff.consecutiveDays} consecutive days increases fatigue risk`}><span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium cursor-help">{staff.consecutiveDays}d straight</span></Tooltip>}
                        {staff.fatigueWarning && <Tooltip content="High fatigue risk — insufficient rest between shifts, may breach WHS requirements"><span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium cursor-help">Fatigue risk</span></Tooltip>}
                        {!staff.eligible && <Tooltip content="Staff is blocked from rostering — check credentials or compliance status"><span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium cursor-help">Blocked</span></Tooltip>}
                      </div>
                    )}
                  </button>
                );
              })}
              {filteredStaff.length === 0 && !loadingStaff && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-5 h-5 text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500">No eligible staff available</p>
                  <p className="text-xs text-gray-400 mt-1">Try a different role or shift</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// SHIFT DETAIL / EDIT MODAL — Enhanced
// ═══════════════════════════════════════════════════════════════════

const ShiftDetailModal = ({ entry, facilityId, onClose, onUpdated }) => {
  const [handoverNotes, setHandoverNotes] = useState(entry.handoverNotes || '');
  const [editNotes, setEditNotes] = useState(entry.notes || '');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);

  const handleSaveNotes = async () => {
    await RosterService.updateShift(entry.id, { notes: editNotes });
    onUpdated();
  };

  const handleSubmitHandover = async () => {
    await RosterService.submitHandover(entry.id, handoverNotes, entry.staffName);
    onUpdated();
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);
    await RosterService.confirmShift(entry.id, entry.staffId);
    setConfirmLoading(false);
    onUpdated();
  };

  const handleComplete = async () => {
    setCompleteLoading(true);
    await RosterService.completeShift(entry.id, {
      hoursWorked: entry.hoursScheduled,
      handoverNotes
    });
    setCompleteLoading(false);
    onUpdated();
  };

  const roleColor = ROLE_COLORS[entry.careMinuteCategory] || ROLE_COLORS.other;
  const accent = SHIFT_ACCENT[entry.shift] || SHIFT_ACCENT.AM;
  const initials = entry.staffName?.split(' ').map(n => n[0]).join('') || '?';
  const shiftInfo = SHIFTS[entry.shift];
  const estimatedCost = shiftInfo ? (entry.careMinuteCategory === 'rn' || entry.careMinuteCategory === 'en' ? 38.07 : 32.86) * (entry.hoursScheduled || 8) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Banner Header */}
        <div className={`${accent.bg} px-6 py-5 relative`}>
          <button onClick={onClose} className="absolute top-3 right-3 p-2 bg-white/20 hover:bg-white/30 rounded-xl text-white transition-all"><X className="w-4 h-4" /></button>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl ${roleColor.avatar} flex items-center justify-center text-white text-lg font-bold shadow-lg`}>
              {initials}
            </div>
            <div className="text-white">
              <h2 className="text-lg font-bold">{entry.staffName || 'Open Shift'}</h2>
              <p className="text-sm opacity-90">{entry.roleName} &bull; {entry.ward}</p>
            </div>
          </div>

          {/* Timeline Bar */}
          {shiftInfo && (
            <div className="mt-4 bg-white/20 rounded-lg p-2">
              <div className="flex items-center justify-between text-xs text-white/80 mb-1">
                <span>{entry.startTime || shiftInfo.start}</span>
                <span className="font-bold">{entry.hoursScheduled}h shift</span>
                <span>{entry.endTime || shiftInfo.end}</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white/60 rounded-full" style={{ width: entry.status === 'completed' ? '100%' : entry.status === 'in_progress' ? '60%' : '0%' }} />
              </div>
            </div>
          )}
        </div>

        <div className="p-5 space-y-4">
          {/* Info Grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-xs text-gray-500">Date</div>
              <div className="font-semibold text-sm mt-0.5">{entry.date}</div>
            </div>
            <div className={`${accent.light} rounded-xl p-3 text-center border ${accent.border}`}>
              <div className="text-xs text-gray-500">Shift</div>
              <div className="font-semibold text-sm mt-0.5">{entry.shiftName}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-xs text-gray-500">Status</div>
              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold mt-0.5 ${STATUS_COLORS[entry.status] || ''}`}>{entry.status}</span>
            </div>
          </div>

          {/* Staff Details */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Employment:</span> <span className={`font-medium px-2 py-0.5 rounded-full text-xs ${EMPLOYMENT_BADGES[entry.employmentType] || ''}`}>{entry.employmentType}</span></div>
              <div><span className="text-gray-500">Hours:</span> <span className="font-medium">{entry.hoursScheduled}h</span></div>
              {entry.confirmedAt && <div className="col-span-2"><span className="text-gray-500">Confirmed:</span> <span className="font-medium">{new Date(entry.confirmedAt).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>}
            </div>
            {/* Cost Estimate */}
            <div className="pt-2 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-500 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Est. cost</span>
              <span className="font-bold text-gray-900">${estimatedCost.toFixed(2)}</span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Shift Notes</label>
            <div className="flex gap-2">
              <input value={editNotes} onChange={e => setEditNotes(e.target.value)} className="flex-1 px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500" placeholder="Add notes..." />
              <button onClick={handleSaveNotes} className="px-4 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm transition-all flex items-center gap-1"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>

          {/* Handover */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Handover Notes</label>
            <textarea value={handoverNotes} onChange={e => setHandoverNotes(e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500" placeholder="Clinical handover notes for incoming shift..." />
            <button onClick={handleSubmitHandover} className="mt-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all">
              <FileText className="w-4 h-4" /> Submit Handover
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-3 border-t border-gray-200">
            {entry.status === 'published' && (
              <button onClick={handleConfirm} disabled={confirmLoading} className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all shadow-sm">
                {confirmLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {confirmLoading ? 'Confirming...' : 'Confirm Shift'}
              </button>
            )}
            {(entry.status === 'confirmed' || entry.status === 'in_progress') && (
              <button onClick={handleComplete} disabled={completeLoading} className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all shadow-sm">
                {completeLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {completeLoading ? 'Completing...' : 'Complete Shift'}
              </button>
            )}
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl font-medium text-sm hover:bg-gray-50 transition-all">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roster;
